/**
 * The **numbers** widget (`specs/M10-D2-widgets.md` §5.3): one aggregate over one column,
 * rendered big. `columnId: null` with `func: 'count'` is *number of items*, the default and the
 * only configuration that needs no column at all.
 */
import { z } from 'zod';
import { COLUMN_AGGREGATIONS } from '../../enums.js';
import { defineWidget } from '../types.js';
export const numbersWidgetSettingsSchema = z.object({
    columnId: z.string().nullable().default(null),
    func: z.enum(COLUMN_AGGREGATIONS).default('count'),
    boardIds: z.array(z.string()).default([]),
    unit: z.string().nullable().default(null),
    extra: z.record(z.string(), z.unknown()).default({}),
});
export const numbersWidget = defineWidget({
    id: 'numbers',
    kind: 'NUMBER',
    title: 'Numbers',
    description: 'One number: a count, sum, average, min or max over a column.',
    settingsSchema: numbersWidgetSettingsSchema,
    defaultSettings: numbersWidgetSettingsSchema.parse({}),
    dataShape: 'scalar',
    defaultLayout: { x: 0, y: 0, w: 3, h: 3 },
    minLayout: { w: 2, h: 2 },
    requiresBoards: true,
    implemented: true,
    plannedIn: null,
    galleryOrder: 2,
    fromMondaySettings(raw) {
        const source = asObject(raw);
        const known = new Set(['column_id', 'func', 'board_ids', 'unit']);
        const extra = {};
        for (const [key, value] of Object.entries(source))
            if (!known.has(key))
                extra[key] = value;
        const func = String(source['func'] ?? 'count');
        return numbersWidgetSettingsSchema.parse({
            columnId: typeof source['column_id'] === 'string' ? source['column_id'] : null,
            func: COLUMN_AGGREGATIONS.includes(func) ? func : 'count',
            boardIds: Array.isArray(source['board_ids']) ? source['board_ids'].map(String) : [],
            unit: typeof source['unit'] === 'string' ? source['unit'] : null,
            extra,
        });
    },
    toMondaySettings(settings) {
        const out = { column_id: settings.columnId, func: settings.func };
        if (settings.boardIds.length > 0)
            out['board_ids'] = settings.boardIds;
        if (settings.unit !== null)
            out['unit'] = settings.unit;
        for (const [key, value] of Object.entries(settings.extra))
            out[key] = value;
        return out;
    },
    dataRequest(settings) {
        return {
            dimension: { kind: 'none' },
            dateBucket: 'month',
            measures: [{ key: 'value', columnId: settings.columnId, func: settings.func }],
            includeEmpty: false,
            boardIds: settings.boardIds,
        };
    },
});
function asObject(raw) {
    if (typeof raw === 'string') {
        try {
            const parsed = JSON.parse(raw);
            return parsed !== null && typeof parsed === 'object' ? parsed : {};
        }
        catch {
            return {};
        }
    }
    return raw !== null && typeof raw === 'object' ? raw : {};
}
//# sourceMappingURL=index.js.map